/**
 * Vocabularies для Block 13 «Договор на мебель»: regions, languages, currency codes.
 *
 * 5 language variants total = 3 regions × {primary lang, optional secondary lang}:
 *   - KZ: ru + kk
 *   - RU: ru only
 *   - UZ: ru + uz
 *
 * `CONTRACT_TEMPLATE_KEYS` — composite "{region}.{lang}" keys matching JSON files in
 * `apps/web/src/features/contract/templates/`. Source of truth для Phase 2 PDF
 * рендера (template lookup по key).
 *
 * References:
 *   - ADR-0025 «Block 13 contract architecture» §Q1 (templates as structured JSON)
 *   - PRD `docs/prd/v4/11-block-13-contract.md` Экран 2.13.2 «Язык договора»
 *   - Reference pattern: `domain/quote-furniture-types-vocabulary.ts`
 */

export const CONTRACT_REGIONS = ['kz', 'ru', 'uz'] as const;
export type ContractRegion = (typeof CONTRACT_REGIONS)[number];

export const CONTRACT_LANGUAGES = ['ru', 'kk', 'uz'] as const;
export type ContractLanguage = (typeof CONTRACT_LANGUAGES)[number];

/** Composite key `{region}.{lang}` для template lookup. */
export const CONTRACT_TEMPLATE_KEYS = ['kz.ru', 'kz.kk', 'ru.ru', 'uz.ru', 'uz.uz'] as const;
export type ContractTemplateKey = (typeof CONTRACT_TEMPLATE_KEYS)[number];

/** Доступные языки на регион (для UI chip-segment в Экран 2.13.2). */
export const CONTRACT_LANGUAGES_PER_REGION: Record<ContractRegion, readonly ContractLanguage[]> = {
  kz: ['ru', 'kk'],
  ru: ['ru'],
  uz: ['ru', 'uz'],
};

/** Default language per region (preselected при создании договора). */
export const CONTRACT_DEFAULT_LANGUAGE_PER_REGION: Record<ContractRegion, ContractLanguage> = {
  kz: 'ru',
  ru: 'ru',
  uz: 'ru',
};

/** Currency codes ISO 4217 per region. */
export const CONTRACT_CURRENCY_CODES = ['KZT', 'RUB', 'UZS'] as const;
export type ContractCurrencyCode = (typeof CONTRACT_CURRENCY_CODES)[number];

export const CONTRACT_CURRENCY_PER_REGION: Record<ContractRegion, ContractCurrencyCode> = {
  kz: 'KZT',
  ru: 'RUB',
  uz: 'UZS',
};

/**
 * Phase 6.5 Amendment A1 (2026-05-24) — currency display helpers.
 *
 * Используются UI (input labels, ИТОГО card) и PDF render (renderContractToPdf)
 * для derive symbol/word из user-controlled `ContractDraft.currencyCode` —
 * вместо template.meta.currency / currencyShort (которые ранее были
 * template-locked). Decoupling позволяет KZ furniture maker оформить RUB
 * контракт для RU клиента без смены языка договора.
 */
export const CONTRACT_CURRENCY_SHORT: Record<ContractCurrencyCode, string> = {
  KZT: '₸',
  RUB: '₽',
  UZS: 'сум',
};

export const CONTRACT_CURRENCY_WORD: Record<ContractCurrencyCode, string> = {
  KZT: 'тенге',
  RUB: 'рубль',
  UZS: 'сум',
};

/**
 * Phase 7 hotfix round 4 (R4-#2, 2026-05-26) — grammar-aware currency label
 * для PDF суммовых цифр («500 000 рублей»). Отличается от `CONTRACT_CURRENCY_WORD`
 * только формой RUB: тут genitive plural «рублей» вместо nominative singular
 * «рубль». KZT / UZS не склоняются → совпадают.
 *
 * **Зачем отдельный helper:** `getCurrencyWord` frozen — используется в UI
 * chip-segment SectionCostAndTerms + ИТОГО card SectionSpec, где legacy строка
 * «рубль» осталась с Phase 6.5B. PDF render — production document — требует
 * грамматически правильную форму. Разделение helper'ов изолирует PDF concern
 * без breaking change UI labels.
 *
 * **Используется только в:** `renderContractToPdf.ts` (Итого) +
 * `contractPdfDrawing.drawSpecCostRow` (block_cost cell в Приложении №1).
 * Главная страница Сумма НЕ trogается — там прописная сумма из
 * `amountInWords` уже включает word внутри.
 */
export const CONTRACT_CURRENCY_LABEL: Record<ContractCurrencyCode, string> = {
  KZT: 'тенге',
  RUB: 'рублей', // genitive plural per русская грамматика «500 000 рублей»
  UZS: 'сум',
};

/** Short symbol для display (₸ / ₽ / сум). */
export function getCurrencyShort(code: ContractCurrencyCode): string {
  return CONTRACT_CURRENCY_SHORT[code];
}

/** Full word for prose context (e.g. «500 000 тенге»). */
export function getCurrencyWord(code: ContractCurrencyCode): string {
  return CONTRACT_CURRENCY_WORD[code];
}

/**
 * Phase 7 hotfix round 4 (R4-#2) — grammar-correct currency label для PDF
 * сумм цифрами. Используется ТОЛЬКО в PDF render (контрактный документ).
 * UI labels продолжают использовать `getCurrencyWord` (frozen). См. JSDoc
 * `CONTRACT_CURRENCY_LABEL` выше.
 */
export function getCurrencyLabel(code: ContractCurrencyCode): string {
  return CONTRACT_CURRENCY_LABEL[code];
}

/** Type guards — для runtime валидации входящих query params / URLs. */
export function isContractRegion(value: unknown): value is ContractRegion {
  return typeof value === 'string' && (CONTRACT_REGIONS as readonly string[]).includes(value);
}

export function isContractLanguage(value: unknown): value is ContractLanguage {
  return typeof value === 'string' && (CONTRACT_LANGUAGES as readonly string[]).includes(value);
}

export function isContractTemplateKey(value: unknown): value is ContractTemplateKey {
  return typeof value === 'string' && (CONTRACT_TEMPLATE_KEYS as readonly string[]).includes(value);
}

/** Builds composite `{region}.{lang}` key + validates combination. */
export function buildContractTemplateKey(
  region: ContractRegion,
  lang: ContractLanguage,
): ContractTemplateKey {
  const candidate = `${region}.${lang}` as ContractTemplateKey;
  if (!isContractTemplateKey(candidate)) {
    throw new Error(
      `INVALID_CONTRACT_TEMPLATE_KEY: region=${region}, lang=${lang} (valid keys: ${CONTRACT_TEMPLATE_KEYS.join(', ')})`,
    );
  }
  return candidate;
}
